import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Shield, Upload, FileText, CheckCircle2, Clock, AlertCircle, XCircle,
  Phone, MapPin, FileCheck, ChevronRight, Info, RefreshCw, Trash2, Ban
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { usersService, uploadService } from '../../services/api';
import DashboardLayout from '../../components/DashboardLayout';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo

// Configuration des statuts KYC (dérivés du proStatus backend)
const KYC_STATUS_CONFIG = {
  non_soumis: {
    label: 'Non soumis',
    color: 'bg-slate-100 text-slate-700',
    icon: FileText,
    description: "Votre dossier KYC n'a pas encore été soumis.",
  },
  en_attente_validation: {
    label: 'En cours de vérification',
    color: 'bg-amber-100 text-amber-800',
    icon: Clock,
    description: 'Votre dossier est en cours de vérification par notre équipe.',
  },
  valide: {
    label: 'Validé',
    color: 'bg-green-100 text-green-800',
    icon: CheckCircle2,
    description: 'Félicitations ! Votre profil professionnel a été vérifié.',
  },
  refuse: {
    label: 'Refusé',
    color: 'bg-red-100 text-red-800',
    icon: XCircle,
    description: 'Votre dossier a été refusé. Vous pouvez soumettre de nouveaux documents.',
  },
  suspendu: {
    label: 'Suspendu',
    color: 'bg-red-100 text-red-800',
    icon: Ban,
    description: 'Votre compte est suspendu. Contactez le support pour plus d\'informations.',
  },
};

// Statut des documents individuels (KycStatus backend)
const DOC_STATUS_CONFIG = {
  PENDING: { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
  APPROVED: { label: 'Validé', color: 'bg-green-100 text-green-800' },
  REJECTED: { label: 'Refusé', color: 'bg-red-100 text-red-800' },
};

// Types de documents alignés sur l'enum KycDocType du backend
const DOCUMENT_TYPES = [
  {
    value: 'ID_CARD',
    label: "Pièce d'identité",
    description: "Carte d'identité ou passeport (recto/verso)",
  },
  {
    value: 'BUSINESS_LICENSE',
    label: 'Extrait Kbis / Licence',
    description: 'Document officiel de moins de 3 mois',
  },
  {
    value: 'INSURANCE',
    label: "Attestation d'assurance RC Pro",
    description: 'En cours de validité',
  },
  {
    value: 'CERTIFICATION',
    label: 'Certificat de qualification',
    description: 'Qualibat, RGE, etc.',
  },
];

const deriveStatus = (kycDocuments = [], proStatus) => {
  if (proStatus === 'SUSPENDED') return 'suspendu';

  const hasApproved = kycDocuments.some((doc) => doc.status === 'APPROVED');
  const hasPending = kycDocuments.some((doc) => doc.status === 'PENDING');
  const hasRejected = kycDocuments.some((doc) => doc.status === 'REJECTED');

  if (hasApproved) return 'valide';
  if (hasPending) return 'en_attente_validation';
  if (hasRejected || proStatus === 'REJECTED') return 'refuse';
  if (proStatus === 'PENDING') return 'en_attente_validation';

  return 'non_soumis';
};

export default function MyKYC() {
  const { refreshUser, hasModule } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [proStatus, setProStatus] = useState('NONE');
  const [submittedDocuments, setSubmittedDocuments] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);

  // Champs de contact persistés dans le profil utilisateur
  const [profileForm, setProfileForm] = useState({
    telephone: '',
    ville: '',
    description: '',
  });

  useEffect(() => {
    loadKYCData();
  }, []);

  const loadKYCData = async () => {
    setLoading(true);
    try {
      const response = await usersService.getMyProfile();
      const data = response?.data ?? response;
      setProStatus(data.proStatus ?? 'NONE');
      setSubmittedDocuments(data.kycDocuments ?? []);
      setProfileForm({
        telephone: data.phone ?? '',
        ville: data.city ?? '',
        description: data.bio ?? '',
      });
    } catch (error) {
      console.error('Error loading KYC:', error);
      toast.error(error?.message || 'Erreur lors du chargement du dossier KYC');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error('Le fichier est trop volumineux (max 10 Mo)');
      e.target.value = '';
      return;
    }

    setUploadingDoc(docType);
    try {
      const response = await uploadService.uploadKYCDocument(file);
      const url = response?.data?.url ?? response?.url;
      if (!url) throw new Error('URL du fichier introuvable');

      setPendingDocuments((prev) => [
        ...prev.filter((d) => d.type !== docType),
        { type: docType, fileUrl: url, filename: file.name },
      ]);
      toast.success('Document téléversé');
    } catch (error) {
      toast.error(error?.message || "Erreur lors de l'upload");
    } finally {
      setUploadingDoc(null);
      e.target.value = '';
    }
  };

  const handleRemovePendingDocument = (docType) => {
    setPendingDocuments((prev) => prev.filter((d) => d.type !== docType));
  };

  const handleSubmitKYC = async () => {
    if (pendingDocuments.length === 0) {
      toast.error('Veuillez téléverser au moins un document');
      return;
    }

    setSubmitting(true);
    try {
      await usersService.updateMyProfile({
        telephone: profileForm.telephone,
        ville: profileForm.ville,
        bio: profileForm.description,
      });

      await usersService.submitKYC(
        pendingDocuments.map(({ type, fileUrl }) => ({ type, fileUrl }))
      );

      toast.success('Dossier KYC soumis avec succès !');
      setPendingDocuments([]);
      await refreshUser();
      await loadKYCData();
    } catch (error) {
      toast.error(error?.message || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const status = deriveStatus(submittedDocuments, proStatus);
  const statusConfig = KYC_STATUS_CONFIG[status];
  const StatusIcon = statusConfig.icon;
  const canEdit = ['non_soumis', 'refuse'].includes(status);
  const isValidated = status === 'valide';
  const approvedCount = submittedDocuments.filter((d) => d.status === 'APPROVED').length;

  const submittedAt = submittedDocuments.length
    ? submittedDocuments
        .map((d) => new Date(d.createdAt))
        .sort((a, b) => b - a)[0]
    : null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[hsl(var(--primary))] border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!hasModule('kyc_submission_access')) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Accès restreint</h2>
          <p className="text-slate-600 mb-4">
            Passez en mode Professionnel pour accéder à la vérification KYC.
          </p>
          <Link to="/tableau-de-bord/profil">
            <Button>Gérer mon mode de compte</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/tableau-de-bord" className="hover:text-[hsl(var(--primary))]">Tableau de bord</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 font-medium">Vérification KYC</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary)/0.08)] flex items-center justify-center">
              <Shield className="w-6 h-6 text-[hsl(var(--primary))]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Vérification KYC</h1>
              <p className="text-slate-600">Faites vérifier votre profil professionnel</p>
            </div>
          </div>
        </div>

        {/* Statut actuel */}
        <div className={`rounded-2xl p-6 mb-8 shadow-soft ${
          isValidated ? 'bg-green-50 border border-green-200' :
          (status === 'refuse' || status === 'suspendu') ? 'bg-red-50 border border-red-200' :
          status === 'en_attente_validation' ? 'bg-amber-50 border border-amber-200' :
          'bg-slate-50 border border-slate-200'
        }`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isValidated ? 'bg-green-100' :
              (status === 'refuse' || status === 'suspendu') ? 'bg-red-100' :
              status === 'en_attente_validation' ? 'bg-amber-100' :
              'bg-slate-200'
            }`}>
              <StatusIcon className={`w-6 h-6 ${
                isValidated ? 'text-green-600' :
                (status === 'refuse' || status === 'suspendu') ? 'text-red-600' :
                status === 'en_attente_validation' ? 'text-amber-600' :
                'text-slate-500'
              }`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="font-semibold text-lg text-slate-900">Statut de votre dossier</h2>
                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
              </div>
              <p className="text-slate-600">{statusConfig.description}</p>
            </div>
          </div>
        </div>

        {/* Badge Pro vérifié si validé */}
        {isValidated && (
          <div className="bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--primary-hover))] rounded-2xl p-6 mb-8 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">Vous êtes un Pro vérifié !</h3>
                <p className="text-white/90">
                  Le badge "Pro vérifié" apparaît sur votre profil et dans l'annuaire des artisans.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Formulaire */}
        {canEdit && (
          <>
            {/* Informations de contact */}
            <div className="lux-dashboard-panel p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Phone className="w-5 h-5 text-[hsl(var(--primary))]" />
                Informations de contact
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                Ces informations sont enregistrées sur votre profil et facilitent la vérification.
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="telephone">Téléphone professionnel</Label>
                  <Input
                    id="telephone"
                    name="telephone"
                    placeholder="01 23 45 67 89"
                    value={profileForm.telephone}
                    onChange={handleProfileChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="ville">Ville</Label>
                  <Input
                    id="ville"
                    name="ville"
                    placeholder="Paris"
                    value={profileForm.ville}
                    onChange={handleProfileChange}
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="description">Description de l'activité</Label>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder="Décrivez votre activité, vos spécialités..."
                    value={profileForm.description}
                    onChange={handleProfileChange}
                    className="mt-1"
                    rows={3}
                    maxLength={500}
                  />
                  <p className="text-xs text-slate-400 mt-1">{profileForm.description.length}/500 caractères</p>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="lux-dashboard-panel p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-[hsl(var(--primary))]" />
                Documents justificatifs
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                Téléversez les documents nécessaires à la vérification de votre profil professionnel.
              </p>

              <div className="space-y-4">
                {DOCUMENT_TYPES.map((docType) => {
                  const pendingDoc = pendingDocuments.find((d) => d.type === docType.value);
                  const submittedDoc = submittedDocuments.find((d) => d.type === docType.value);
                  const isUploading = uploadingDoc === docType.value;
                  const isDone = pendingDoc || submittedDoc?.status === 'APPROVED';

                  return (
                    <div
                      key={docType.value}
                      className={`border rounded-xl p-4 ${
                        isDone ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isDone ? 'bg-green-100' : 'bg-slate-200'
                          }`}>
                            {isDone ? (
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                            ) : (
                              <FileText className="w-5 h-5 text-slate-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{docType.label}</p>
                            <p className="text-sm text-slate-500">{docType.description}</p>
                            {pendingDoc && (
                              <p className="text-xs text-green-600 mt-1">
                                {pendingDoc.filename} • Prêt à soumettre
                              </p>
                            )}
                            {!pendingDoc && submittedDoc && (
                              <div className="mt-1">
                                <Badge className={DOC_STATUS_CONFIG[submittedDoc.status]?.color}>
                                  {DOC_STATUS_CONFIG[submittedDoc.status]?.label}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {pendingDoc ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemovePendingDocument(docType.value)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          ) : (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                className="hidden"
                                onChange={(e) => handleFileUpload(e, docType.value)}
                                disabled={isUploading}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="pointer-events-none"
                                disabled={isUploading}
                              >
                                {isUploading ? (
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4 mr-2" />
                                )}
                                {isUploading ? 'Upload...' : submittedDoc ? 'Remplacer' : 'Téléverser'}
                              </Button>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Formats acceptés</p>
                    <p>PDF, JPG, PNG, WebP (max 10 Mo par fichier)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bouton de soumission */}
            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={() => navigate('/tableau-de-bord')}>
                Annuler
              </Button>
              <Button
                onClick={handleSubmitKYC}
                disabled={submitting || pendingDocuments.length === 0}
                className="btn-clickpro-primary"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Soumission...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Soumettre mon dossier KYC
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {/* Si en attente de validation */}
        {status === 'en_attente_validation' && (
          <div className="lux-dashboard-panel p-8 text-center">
            <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Dossier en cours de vérification</h3>
            <p className="text-slate-600 max-w-md mx-auto mb-6">
              Notre équipe examine votre dossier. Vous recevrez une notification dès que la vérification sera terminée.
            </p>

            {submittedDocuments.length > 0 && (
              <div className="max-w-md mx-auto space-y-2 mb-6 text-left">
                {submittedDocuments.map((doc) => {
                  const typeLabel = DOCUMENT_TYPES.find((t) => t.value === doc.type)?.label || doc.type;
                  return (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-700">{typeLabel}</span>
                      <Badge className={DOC_STATUS_CONFIG[doc.status]?.color}>
                        {DOC_STATUS_CONFIG[doc.status]?.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}

            {submittedAt && (
              <p className="text-sm text-slate-500">
                Soumis le {submittedAt.toLocaleDateString('fr-FR', {
                  day: 'numeric', month: 'long', year: 'numeric',
                })}
              </p>
            )}
          </div>
        )}

        {/* Récapitulatif si validé */}
        {isValidated && (
          <div className="lux-dashboard-panel p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Informations vérifiées</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Phone className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="text-xs text-slate-500">Téléphone</p>
                  <p className="font-medium text-slate-900">{profileForm.telephone || 'Non renseigné'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <MapPin className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="text-xs text-slate-500">Ville</p>
                  <p className="font-medium text-slate-900">{profileForm.ville || 'Non renseigné'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg md:col-span-2">
                <FileCheck className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="text-xs text-slate-500">Documents validés</p>
                  <p className="font-medium text-slate-900">{approvedCount} document(s)</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
